import { useTotalesDeLaOrden } from './useTotalesDeLaOrden'
import { useRecorrido } from '@/modules/tablero/useRecorrido'
import { ESTADOS_ORDEN_SERVICIO } from '@/shared/dominio/estados'
import { pesos } from '@/shared/utilidades/formato'

import type { OrdenServicioConRelaciones } from './tipos'
import estilos from './CabeceraOrden.module.css'

/**
 * LA CABECERA DE UNA ORDEN · quién, dónde va y cuánto lleva.
 *
 * Antes esto era una banda lila con el nombre del cliente a 14 px y debajo una
 * tabla de filas gris sobre blanco. La pantalla del documento más importante
 * del sistema no decía UNA SOLA CIFRA: ni cuánto vale, ni cuánto lleva parado,
 * ni en qué punto del recorrido está. Toda esa información existía y había que
 * ir a buscarla a otras tres pantallas.
 *
 * Ahora la cabecera contesta las tres preguntas que se hacen al abrir una
 * orden, en el orden en que se hacen:
 *
 *   1. ¿De quién es y qué es?          → el nombre, grande.
 *   2. ¿Dónde va y cuánto lleva ahí?   → las cifras y la barra de estaciones.
 *   3. ¿Cuánto vale?                   → el valor, o que todavía no lo tiene.
 *
 * LA BARRA DE ESTACIONES ES EL DATO, no un adorno de progreso. Nueve casillas,
 * las de compuerta marcadas: son las dos donde el trabajo no avanza solo
 * —alguien tiene que aprobar— y por eso son donde se atasca. Verlas en la
 * ficha evita el viaje al tablero para entender por qué esto lleva doce días.
 */

/**
 * LAS NUEVE, SIEMPRE LAS NUEVE.
 *
 * La vista solo devuelve las estaciones por las que la orden YA pasó, así que
 * una recién creada traía una sola y la barra dibujaba un noveno de recorrido:
 * parecía un fallo de datos, no una orden que acaba de empezar. El recorrido de
 * MEGA tiene nueve etapas fijas —están en la base desde 0046— y hay que verlas
 * todas para entender cuánto falta.
 *
 * Las dos compuertas van marcadas: son donde el trabajo NO avanza solo porque
 * alguien tiene que aprobar, y por lo mismo donde se queda quieto.
 */
const RECORRIDO: { estacion: number; nombre: string; compuerta: boolean }[] = [
  { estacion: 1, nombre: 'Orden de servicio', compuerta: false },
  { estacion: 2, nombre: 'Cotización', compuerta: false },
  { estacion: 3, nombre: 'Aprobación', compuerta: true },
  { estacion: 4, nombre: 'Programación', compuerta: false },
  { estacion: 5, nombre: 'Ejecución', compuerta: false },
  { estacion: 6, nombre: 'Precierre', compuerta: true },
  { estacion: 7, nombre: 'Cierre', compuerta: false },
  { estacion: 8, nombre: 'Acta y firma', compuerta: false },
  { estacion: 9, nombre: 'Servicio finalizado', compuerta: false },
]

/** Las horas en crudo no se leen: a nadie le dice nada «287,4 h». */
function cuantoLleva(horas: number | null): string {
  if (horas === null || horas < 0) return '—'
  if (horas < 1) return 'menos de una hora'
  if (horas < 48) return `${Math.round(horas)} h`
  return `${(horas / 24).toFixed(1).replace('.', ',')} días`
}

export function CabeceraOrden({ orden }: { orden: OrdenServicioConRelaciones }) {
  const { recorrido } = useRecorrido(orden.id)
  const presentacion = ESTADOS_ORDEN_SERVICIO[orden.estado]

  const estaciones = recorrido?.estaciones ?? []
  const actual = recorrido?.estacion_actual ?? null
  /*
   * EL VALOR DE LA CABECERA ES EL DE LA OBRA, NO EL DE UN DOCUMENTO.
   *
   * Mostraba solo la cotización principal, así que al enlazar un adicional o
   * una factura la cifra que todo el mundo mira primero se quedaba corta: lo
   * demás aparecía sumado aparte, más abajo, como si no fuera de la misma
   * obra. Johan lo señaló con un círculo rojo sobre este número.
   *
   * Entra lo aprobado y lo que ya tiene documento en World Office —enlazarlo
   * es decir que va—; lo que nació aquí y espera decisión se cuenta aparte y
   * se avisa debajo, para no inflar la cifra con plata que puede caerse.
   *
   * Y desde 0231 también lo facturado: la cotización que se convirtió en
   * factura cuenta una vez, como factura.
   */
  const { totales } = useTotalesDeLaOrden(orden.id)
  const delDocumento = orden.cotizacion?.valor_total ?? null
  const valor = totales ? totales.aprobado : delDocumento
  const hayMasDeUno = (totales?.documentos ?? 0) > 1

  return (
    <header className={estilos.cabecera}>
      <div className={estilos.identidad}>
        <div className={estilos.quien}>
          <h2 className={estilos.cliente}>{orden.cliente?.nombre ?? 'Sin cliente'}</h2>
          <p className={estilos.servicio}>
            {orden.servicio?.codigo && (
              <span className={estilos.codigo}>{orden.servicio.codigo}</span>
            )}
            {orden.servicio?.nombre ?? 'Sin servicio'}
          </p>
        </div>

        {/* El número no se repite: ya está en la cabecera de la tarjeta, dos
            centímetros más arriba. Aquí solo va el estado, que es lo que
            cambia y lo que hay que poder ver de reojo. */}
        <span
          className={`${estilos.estado} ${estilos[presentacion.tono] ?? ''}`}
          title={presentacion.explicacion}
        >
          {presentacion.etiqueta}
        </span>
      </div>

      {/* Las cifras. El valor manda: es lo que se mira primero de una orden. */}
      <div className={estilos.cifras}>
        <div className={estilos.cifra}>
          <span className={estilos.cifraRotulo}>Valor</span>
          {valor ? (
            <b className={estilos.cifraPlata}>{pesos(valor)}</b>
          ) : (
            <b className={estilos.cifraVacia}>Sin precio</b>
          )}
          <span className={estilos.cifraPie}>
            {/* Con un solo documento, su número. Con varios, cuántos suman:
                repetir el número de la principal haría pensar que la cifra es
                solo de ella. */}
            {hayMasDeUno
              ? `${totales!.documentos} documentos`
              : (orden.cotizacion?.numero_cotizacion ?? 'Todavía no se ha cotizado')}
          </span>

          {/* Lo que espera decisión no se mete en la cifra, pero tampoco se
              esconde: aquí es donde alguien se pregunta si falta algo. */}
          {(totales?.en_propuesta ?? 0) > 0 && (
            <span className={estilos.cifraPendiente}>
              + {pesos(totales!.en_propuesta)} por aprobar
            </span>
          )}
        </div>

        <div className={estilos.cifra}>
          <span className={estilos.cifraRotulo}>Lleva aquí</span>
          <b className={estilos.cifraValor}>{cuantoLleva(recorrido?.horas_en_estacion ?? null)}</b>
          <span className={estilos.cifraPie}>
            {recorrido?.estacion_actual_nombre ?? 'Sin recorrido registrado'}
          </span>
        </div>

        <div className={estilos.cifra}>
          <span className={estilos.cifraRotulo}>Desde que entró</span>
          <b className={estilos.cifraValor}>{cuantoLleva(recorrido?.horas_totales ?? null)}</b>
          <span className={estilos.cifraPie}>
            {actual ? `Estación ${actual} de 9` : 'Sin empezar'}
          </span>
        </div>
      </div>

      {/* Las nueve estaciones. Las de compuerta van marcadas: son las dos donde
          el trabajo NO avanza solo, y por lo mismo donde se queda quieto. */}
      <div className={estilos.recorrido}>
          <ol className={estilos.estaciones}>
            {RECORRIDO.map((base) => {
              const visitada = estaciones.find((x) => x.estacion === base.estacion)
              const e = { ...base, ...visitada }
              const pasada = actual !== null && e.estacion < actual
              const aqui = visitada?.en_curso ?? e.estacion === actual
              return (
                <li
                  key={e.estacion}
                  className={[
                    estilos.paso,
                    pasada ? estilos.pasada : '',
                    aqui ? estilos.aqui : '',
                    e.compuerta ? estilos.compuerta : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  title={`${e.estacion}. ${e.nombre}${e.compuerta ? ' · compuerta: aquí alguien tiene que aprobar' : ''}${
                    visitada?.horas ? ` · ${cuantoLleva(visitada.horas)}` : ''
                  }`}
                >
                  <span className={estilos.pasoBarra} />
                  <span className={estilos.pasoNombre}>{e.nombre}</span>
                </li>
              )
            })}
          </ol>
        </div>
    </header>
  )
}
